import { Entry, TextWriter } from "@zip.js/zip.js";
import { DEFAULT_GIFT_LINK } from "src/constants";
import { AnimationJsonData } from "src/types/gift";
import { ensureHttps } from "src/utils/ensureHttps";
import { getEntryBlob, unzip } from "src/utils/zip";

interface GiftMessage {
  giftId: string;
  url: string;
}

const imageFilePattern = /\.(jpg|jpeg|png|gif|bmp|svg|tiff)$/i;

const defaultAnimationJsonData = async (imageEntry: Entry) => {
  const blob = await getEntryBlob(imageEntry);

  if (!blob) {
    return;
  }

  const giftAnimation = await fetch(DEFAULT_GIFT_LINK).then((res) =>
    res.json()
  );

  const jsonData = { ...giftAnimation };

  if (!jsonData?.assets[0]) {
    return;
  }

  jsonData.assets[0] = {
    ...jsonData.assets[0],
    p: URL.createObjectURL(blob),
  };

  return jsonData;
};

const readJsonFromEntry = async (
  entry: Entry
): Promise<AnimationJsonData | undefined> => {
  if (!entry?.getData) {
    throw new Error("Entry getData method is missing");
  }

  const text = await entry.getData(new TextWriter());
  const parsed = JSON.parse(text);

  return parsed;
};

const processImages = async (
  imageEntries: Entry[],
  jsonData: AnimationJsonData
) => {
  if (!imageEntries?.length) {
    throw new Error("No image entries provided");
  }

  if (!jsonData?.assets?.length) {
    throw new Error("No assets provided in json");
  }

  const imagePromises = imageEntries.map(async (entry) => {
    const blob = await getEntryBlob(entry);
    if (blob) {
      return {
        filename: entry.filename,
        url: URL.createObjectURL(blob),
      };
    }

    return { filename: entry.filename, url: "" };
  });

  const images = await Promise.all(imagePromises);

  jsonData.assets = jsonData.assets.map((asset) => {
    const image = images.find((img) => img.filename === asset.u + asset.p);
    if (image) {
      asset.p = image.url;
      asset.u = "";
    }

    return asset;
  });

  return jsonData;
};

const processZipFile = async (file: Blob) => {
  const entries = await unzip(file);
  if (!entries?.length) {
    throw new Error(`Empty or invalid ZIP file`);
  }

  const imageEntries = entries.filter(({ filename }) =>
    imageFilePattern.test(filename)
  );

  const jsonEntry = entries.find(({ filename }) =>
    ["data.json", "animation.json"].some((ext) => filename.endsWith(ext))
  );

  if (jsonEntry) {
    const animationJsonData = await readJsonFromEntry(jsonEntry);
    if (!animationJsonData) {
      throw new Error("Failed to read animation JSON data");
    }

    return await processImages(imageEntries, animationJsonData);
  }

  const finalImageEntry = entries.find(({ filename }) =>
    filename.endsWith("final.png")
  );

  if (finalImageEntry) {
    return await defaultAnimationJsonData(finalImageEntry);
  }

  if (!finalImageEntry) {
    throw new Error("Neither JSON nor final image found in ZIP");
  }

  throw new Error("No valid animation data found");
};

const fetchAndProcessGiftZip = async (url: string, giftId: string) => {
  try {
    const res = await fetch(ensureHttps(url));
    const file = await res.blob();
    const jsonData = await processZipFile(file);

    if (!jsonData) {
      throw new Error("Failed to process ZIP file");
    }

    self.postMessage({ jsonData });
  } catch (error) {
    self.postMessage({
      error: `Gift ZIP load error for giftId = ${giftId}: ${error}`,
    });
  }
};

self.addEventListener("message", async (event: MessageEvent<GiftMessage>) => {
  const { url, giftId } = event.data;
  if (!url) {
    return;
  }

  await fetchAndProcessGiftZip(url, giftId);
});
